import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { isAuthConfigured } from './config';
import { useCurrentUser } from './useCurrentUser';
import type { Permission } from './permissions';
import { Forbidden } from '@/routes/Forbidden';

/**
 * Route guard for a permission (M0-22).
 *
 * ⚠️ This is a usability feature, not a security control. It stops someone
 * being shown a screen they cannot use; it does not stop them reaching the
 * data, because the data only ever arrives from an API that checks the same
 * permission server-side. Never move a check *out* of the API on the grounds
 * that the SPA already does it.
 *
 * Inert when auth is not configured, matching `RequireAuth` — otherwise a local
 * checkout without an app registration could not open any guarded screen.
 */
export function RequirePermission({
  permission,
  children,
}: {
  permission: Permission;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { data, isPending, isError } = useCurrentUser();

  if (!isAuthConfigured) {
    return <>{children}</>;
  }

  if (isPending) {
    return (
      <p className="text-sm text-[var(--color-muted-foreground)]" role="status">
        {t('requirePermission.checkingAccess')}
      </p>
    );
  }

  // An error here is most often a 403: authenticated, but no user record in
  // this account yet. Same outcome as lacking the permission.
  // Optional chain on `permissions` too, not just `data`: a response that is
  // not the shape we expect must fail closed, not throw and take the shell down.
  if (isError || !data?.permissions?.includes(permission)) {
    return <Forbidden />;
  }

  return <>{children}</>;
}

/** Imperative form, for hiding controls rather than whole routes. */
export function useHasPermission(): (permission: Permission) => boolean {
  const { data } = useCurrentUser();
  return (permission) => !isAuthConfigured || (data?.permissions?.includes(permission) ?? false);
}
